import { Close, History } from '@mui/icons-material';
import { Box, Dialog, Drawer, IconButton, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useEffect, useMemo, useState } from 'react';
import { borberBoxShadow } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useLocale } from '../../contexts/locale.context';
import getDayDateRange from '../../utils/dates/getDayDateRange';
import useMedia from '../../utils/useMedia';
import DataTable, { Column, baseRow } from '../DataTable';

type Props<T extends baseRow> = {
  columns: Column<T>[];
  rows: T[];
  name: string;
  setDateRange: (dateRange: { end: number; start: number } | null) => void;
};

const HistoryModal = <T extends baseRow>({ columns, rows, name, setDateRange }: Props<T>) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const { lang } = useLocale();
  const { isMobile } = useMedia();

  const dateRange = useMemo(() => getDayDateRange(date), [date]);

  useEffect(() => {
    if (open) {
      setDateRange(dateRange);
    } else {
      setDateRange(null);
    }
  }, [dateRange, open, setDateRange]);

  return (
    <>
      <IconButton onClick={() => setOpen(true)}>
        <History />
      </IconButton>

      {isMobile ? (
        <Dialog open={open} onClose={() => setOpen(false)} fullScreen>
          <Box minWidth={300} maxWidth={800} pb={2}>
            <Box
              display="flex"
              gap={isMobile ? 1 : 3}
              justifyContent="space-between"
              flexDirection={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              p={isMobile ? 2 : 4}
              position="sticky"
              top={0}
              sx={{ bgcolor: (t) => t.palette.background.paper, zIndex: 2, boxShadow: borberBoxShadow }}
            >
              <Box display="flex" justifyContent="space-between" width="100%">
                <Typography variant="h6" component="span">
                  {t.history[lang]}: {name}
                </Typography>

                {isMobile && (
                  <IconButton onClick={() => setOpen(false)}>
                    <Close />
                  </IconButton>
                )}
              </Box>

              <DatePicker
                label={t.date[lang]}
                value={date}
                onChange={(newValue) => setDate(newValue)}
                renderInput={(params) => <TextField size="small" {...params} />}
                disableFuture
              />
            </Box>

            <DataTable isCard={false} columns={columns} rows={rows} dataTestIdPrefix="history" />
          </Box>
        </Dialog>
      ) : (
        <Drawer anchor="right" open={open} onClose={() => setOpen(false)} sx={{ maxWidth: '100vw' }}>
          <Box minWidth={300} maxWidth={800} pb={2}>
            <Box
              display="flex"
              gap={3}
              justifyContent="space-between"
              alignItems="center"
              p={4}
              position="sticky"
              top={0}
              sx={{ bgcolor: (t) => t.palette.background.paper, zIndex: 2, boxShadow: borberBoxShadow }}
            >
              <Typography variant="h6">
                {t.history[lang]}: {name}
              </Typography>

              <DatePicker
                label={t.date[lang]}
                value={date}
                onChange={(newValue) => setDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
                disableFuture
              />
            </Box>

            <DataTable isCard={false} columns={columns} rows={rows} dataTestIdPrefix="history" />
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default HistoryModal;
